import { Injectable } from "@angular/core";

/** Light or dark page chrome, independent of the colour theme. "system"
 *  follows the OS/browser preference and updates live when it changes. */
export type AppearanceMode = "light" | "dark" | "system";

/** One palette entry. Mirrors the set the dashboard used to own. */
export interface ThemeDef {
  name: string;
  label: string;
  blue: string;
  blueBright: string;
  cyan: string;
  purple: string;
  purple2: string;
  /** The appearance this theme has always come with (Classic light, the
   *  rest dark) - used when no appearance has been chosen yet, and when a
   *  theme is picked from the header's quick menu. */
  light?: boolean;

  /**
   * The two dots shown on the swatch. Deliberately separate from the functional
   * tokens above: a swatch is a LABEL for the theme, not a render of its
   * internals. Binding the dots to blue/purple happened to look right for five
   * themes and wrong for Classic, whose identity is "the light one" — its first
   * dot must be white, not its --blue.
   */
  dot1: string;
  dot2: string;
  /** Set when dot1 is too pale to show an edge against the tray. */
  paleDot?: boolean;
}

/**
 * Owns the app's colour theme: the palette list, the active choice and how it
 * is applied/persisted. Shared by the header's quick theme popover and the
 * Configuration page's Theme Settings tab so both always agree.
 */
@Injectable({
  providedIn: "root",
})
export class ThemeService {
  readonly themes: ThemeDef[] = [
    {
      name: "classic",
      label: "Classic",
      blue: "#2563eb",
      blueBright: "#3b82f6",
      cyan: "#0ea5e9",
      purple: "#1d4ed8",
      purple2: "#1e40af",
      light: true,
      dot1: "#ffffff",
      dot2: "#2563eb",
      paleDot: true,
    },
    {
      // Replaced "Ocean" (blue & violet), which sat too close to Classic.
      name: "brown",
      label: "Brown",
      blue: "#9a6134",
      blueBright: "#c2885a",
      cyan: "#d9b48f",
      purple: "#7b4a2a",
      purple2: "#5e3822",
      dot1: "#9a6134",
      dot2: "#d9b48f",
    },
    {
      name: "emerald",
      label: "Emerald",
      blue: "#10b981",
      blueBright: "#34d399",
      cyan: "#2dd4bf",
      purple: "#06b6d4",
      purple2: "#0891b2",
      dot1: "#10b981",
      dot2: "#06b6d4",
    },
    {
      name: "violet",
      label: "Violet",
      blue: "#8b5cf6",
      blueBright: "#a78bfa",
      cyan: "#c4b5fd",
      purple: "#ec4899",
      purple2: "#db2777",
      dot1: "#8b5cf6",
      dot2: "#ec4899",
    },
    {
      name: "amber",
      label: "Amber",
      blue: "#f59e0b",
      blueBright: "#fbbf24",
      cyan: "#fcd34d",
      purple: "#e11d48",
      purple2: "#be123c",
      dot1: "#f59e0b",
      dot2: "#e11d48",
    },
    {
      name: "slate",
      label: "Slate",
      blue: "#64748b",
      blueBright: "#94a3b8",
      cyan: "#7dd3fc",
      purple: "#0ea5e9",
      purple2: "#0284c7",
      dot1: "#64748b",
      dot2: "#0ea5e9",
    },
  ];

  activeTheme = "classic";
  mode: AppearanceMode = "light";

  private static readonly THEME_KEY = "appTheme";
  private static readonly MODE_KEY = "appThemeMode";
  private readonly systemLight: MediaQueryList | null =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-color-scheme: light)")
      : null;

  constructor() {
    // "system" re-applies when the OS switches between light and dark.
    const onChange = () => {
      if (this.mode === "system") this.applyTheme(this.activeTheme, false);
    };
    if (this.systemLight) {
      if (this.systemLight.addEventListener) {
        this.systemLight.addEventListener("change", onChange);
      } else if ((this.systemLight as any).addListener) {
        (this.systemLight as any).addListener(onChange); // older Safari
      }
    }
  }

  /** Whether the page is currently drawn light (mode resolved). */
  get isLight(): boolean {
    return this.resolveLight(this.mode);
  }

  resolveLight(mode: AppearanceMode): boolean {
    if (mode === "system") {
      return this.systemLight ? this.systemLight.matches : true;
    }
    return mode === "light";
  }
  /** Any component may listen for this to re-render canvas/SVG that samples the palette. */
  static readonly THEME_EVENT = "sa-theme-change";

  restoreTheme(): void {
    let saved: string | null = null;
    let savedMode: string | null = null;
    try {
      saved = localStorage.getItem(ThemeService.THEME_KEY);
      savedMode = localStorage.getItem(ThemeService.MODE_KEY);
    } catch (e) {
      /* private mode — fall through to the default */
    }
    if (saved && ThemeService.RENAMED[saved]) {
      saved = ThemeService.RENAMED[saved];
    }
    if (savedMode === "light" || savedMode === "dark") {
      this.mode = savedMode;
    } else if (savedMode === "system") {
      // "System" is no longer offered - settle on what the device shows now.
      this.mode = this.resolveLight("system") ? "light" : "dark";
    } else {
      // No appearance chosen yet: keep the look the old colour theme gave
      // (Classic light, the others dark).
      const t = this.isKnown(saved)
        ? this.themes.find((x) => x.name === saved)
        : null;
      this.mode = !t || t.light ? "light" : "dark";
    }
    this.applyTheme(
      this.isKnown(saved) ? (saved as string) : ThemeService.DEFAULT_THEME,
      false,
    );
  }

  static readonly DEFAULT_THEME = "classic";

  isKnown(name: string | null): boolean {
    return !!name && this.themes.some((t) => t.name === name);
  }

  /** Themes that were renamed/replaced, so a stored choice still lands on
   *  its successor instead of falling back to Classic. */
  private static readonly RENAMED: { [old: string]: string } = {
    ocean: "brown",
  };

  /**
   * @param withDefaultMode also switch to the theme's own light/dark
   *   appearance - what picking a theme from the header's quick menu has
   *   always done. The Configuration page sets the appearance separately.
   */
  applyTheme(
    name: string,
    persist: boolean = true,
    withDefaultMode: boolean = false,
  ): void {
    const t = this.themes.find((x) => x.name === name) || this.themes[0];
    this.activeTheme = t.name;
    if (withDefaultMode) {
      this.mode = t.light ? "light" : "dark";
    }
    this.applyPalette(t);

    if (persist) {
      try {
        localStorage.setItem(ThemeService.THEME_KEY, t.name);
        if (withDefaultMode) {
          localStorage.setItem(ThemeService.MODE_KEY, this.mode);
        }
      } catch (e) {
        /* not persisted, but the theme still applies for this session */
      }
    }
  }

  setMode(mode: AppearanceMode, persist: boolean = true): void {
    this.mode = mode;
    this.applyTheme(this.activeTheme, false);
    if (persist) {
      try {
        localStorage.setItem(ThemeService.MODE_KEY, mode);
      } catch (e) {
        /* applies for this session only */
      }
    }
  }

  /**
   * Publishes one theme's palette so every component picks it up.
   *
   * Two naming layers, deliberately:
   *   --blue, --blue-bright, …      read straight off <html> by unscoped
   *                                 components such as the sidebar.
   *   --th-blue, --th-blue-bright,  read by components whose own stylesheet is
   *                                 scoped and therefore declares --blue itself
   *                                 (the dashboard's .sa-dash). Those now
   *                                 resolve as `--blue: var(--th-blue, …)`, so
   *                                 the scoped copy no longer shadows us.
   *
   * `light-mode` on <body> keeps working exactly as before, which is what the
   * sidebar's :host-context(.light-mode) and the dashboard both watch for.
   */
  private applyPalette(t: ThemeDef): void {
    const root = document.documentElement.style;
    const pairs: [string, string][] = [
      ["blue", t.blue],
      ["blue-bright", t.blueBright],
      ["cyan", t.cyan],
      ["purple", t.purple],
      ["purple2", t.purple2],
    ];
    for (const [key, value] of pairs) {
      root.setProperty("--" + key, value);
      root.setProperty("--th-" + key, value);
    }

    const light = this.isLight;
    document.body.classList.toggle("light-mode", light);
    // For CSS that varies by theme rather than by a palette token, e.g. the
    // sidebar's per-theme background image (sidebar.component.css).
    document.body.setAttribute("data-sa-theme", t.name);

    // Charts that sample a token at draw time (the dashboard's trend SVG reads
    // --blue-bright through getComputedStyle) need a nudge to redraw.
    window.dispatchEvent(
      new CustomEvent(ThemeService.THEME_EVENT, {
        detail: { theme: t.name, light },
      }),
    );
  }
}
